'use client';

import { useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

export interface UiModStickyScrollProps {
  className?: string;
  children?: React.ReactNode;
}

const defaultClassName = 'sticky';

/**
 * Приклеивает элемент к краю страницы в зависимости от направления прокрутки.
 *
 * Работает на основе position:sticky
 *
 * **Важно!** Установите родительскому элементу класс: relative, flex(или grid)
 *
 * Для настройки css custom property:
 * - offset-top - отступ сверху при прокрутке, пример tw [--offset-top:20]
 * - offset-bottom - отступ снизу при прокрутке, пример tw [--offset-bottom:20]
 * - inactive - отключение механизма, значение 1, пример tw max-md:[--inactive:1]
 */
export function UiModStickyScroll({
  className,
  children
}: UiModStickyScrollProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const oldScrollTop = useRef(0);

  const onScroll = () => {
    if (!rootRef.current) return;
    const { offsetTop, offsetHeight, parentElement } = rootRef.current;
    if (!parentElement || offsetHeight >= parentElement.offsetHeight) return;

    const styles = getComputedStyle(rootRef.current);
    const inactive = Boolean(styles.getPropertyValue('--inactive') === '1');
    if (inactive) {
      rootRef.current.style.top = '';
      rootRef.current.style.bottom = '';
      rootRef.current.style.alignSelf = '';
      rootRef.current.style.marginTop = '';
      return;
    }

    const rect = rootRef.current.getBoundingClientRect();
    const rectParent = parentElement.getBoundingClientRect();
    const scrollTop = Math.ceil(window.scrollY);
    const windowHeight = window.innerHeight;
    const direction = scrollTop < oldScrollTop.current ? 'up' : 'down';
    oldScrollTop.current = scrollTop;

    const getPropertyValue = (key: string) => {
      const div = document.createElement('div');
      div.style.width = styles.getPropertyValue(key);
      return (
        Number(
          div.style.width
            .replace('px', '')
            .replace('calc(', '')
            .replace(')', '')
        ) || 0
      );
    };

    const innerOffsetTop = getPropertyValue('--offset-top');
    const innerOffsetBottom = getPropertyValue('--offset-bottom');

    const isIntervalTop = rectParent.top < innerOffsetTop;
    const isIntervalBottom =
      rectParent.bottom - windowHeight + innerOffsetBottom > 0;

    if (!isIntervalTop || offsetHeight < windowHeight) {
      rootRef.current.style.top = `${innerOffsetTop}px`;
      rootRef.current.style.bottom = '';
      rootRef.current.style.alignSelf = 'start';
      rootRef.current.style.marginTop = '';
      return;
    }

    if (!isIntervalBottom) {
      rootRef.current.style.top = '';
      rootRef.current.style.bottom = `${innerOffsetBottom}px`;
      rootRef.current.style.alignSelf = 'end';
      rootRef.current.style.marginTop = '';
      return;
    }

    if (direction === 'up') {
      if (rect.top < innerOffsetTop) {
        rootRef.current.style.bottom = '';
        rootRef.current.style.alignSelf = rootRef.current.style.marginTop
          ? 'start'
          : 'end';
      } else {
        rootRef.current.style.top = `${innerOffsetTop}px`;
        rootRef.current.style.bottom = '';
        rootRef.current.style.alignSelf = 'start';
        rootRef.current.style.marginTop = ``;
      }
      return;
    }

    const rectBottom = Math.ceil(windowHeight - rect.bottom);
    if (direction === 'down') {
      rootRef.current.style.marginTop = `${offsetTop}px`;
      if (rectBottom < innerOffsetBottom) {
        rootRef.current.style.top = '';
      } else {
        rootRef.current.style.top = '';
        rootRef.current.style.bottom = `${innerOffsetBottom}px`;
        rootRef.current.style.alignSelf = 'end';
      }
      return;
    }
  };

  useEffect(() => {
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    const observer = new ResizeObserver(onScroll);
    if (rootRef.current && rootRef.current.parentElement) {
      observer.observe(rootRef.current.parentElement);
      observer.observe(rootRef.current);
    }
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      observer.disconnect();
    };
  }, []);

  return (
    <div
      className={twMerge(defaultClassName, className)}
      ref={rootRef}
    >
      {children}
    </div>
  );
}
